import { useState, useEffect } from "react";
import {
  AdminBookingUpdateProps,
  StudentFormRes,
  StudentModelProps,
} from "../../types";

export const useSubmitForm = (
  setToggleDrawer: (type: "add" | "update", triggered: boolean) => void,
  updateDetailsFn: AdminBookingUpdateProps["updateStudentDetails"],
  studentIndex: number,
  classExpectation: string,
  requestId: number,
  defaultValues:
    | {
        class?: string;
        id?: number;
        age?: number;
        gender?: string;
        name?: string;
      }
    | undefined,
  refreshRequests: React.Dispatch<React.SetStateAction<number>>
) => {
  const [formValues, setFormValues] = useState<StudentFormRes>({
    name: "",
    age: 0,
    gender: "",
    subjects: [],
    class: "",
    id: 0,
    expectation: classExpectation,
  });
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (defaultValues?.class) {
      setFormValues((_prev) => ({
        ..._prev,
        name: defaultValues.name ?? "",
        age: defaultValues.age ?? 0,
        gender: defaultValues.gender ?? "",
        class: defaultValues.class ?? "",
        id: defaultValues.id ?? 0,
      }));
    }
  }, [defaultValues?.class]); // class is universal among AddStudentDetails and UpdatStudentDetails

  const handleSubmit = async () => {
    setFormError("");
    setLoading(true);
    if (
      !formValues.name ||
      !formValues.age ||
      !formValues.class ||
      !formValues.gender ||
      formValues.subjects.length <= 0
    ) {
      setFormError("All fields are required");
      setLoading(false);
      return;
    }

    try {
      let studentDetails: StudentFormRes = {
        name: formValues.name,
        age: +formValues.age,
        class: formValues.class,
        gender: formValues.gender,
        subjects: formValues.subjects,
        expectation: formValues.expectation,
      };
      if (formValues.id) {
        // including id
        studentDetails = {
          ...formValues,
          age: +formValues.age,
        };
      }
      await updateDetailsFn(studentDetails, requestId, studentIndex);
      setToggleDrawer(defaultValues?.id ? "update" : "add", false);
      refreshRequests(requestId);
    } catch (e) {
      const message = (e as Error).message;
      setFormError(message ?? "Error occurred while performing this action");
    }
    setLoading(false);
  };

  const updateField = (key: string, value: string) => {
    setFormValues((_val) => ({ ..._val, [key]: value }));
  };

  const toDefault = () => {
    setFormValues({
      ...formValues,
      age: 0,
      class: "",
      gender: "",
      subjects: [],
      id: 0,
      name: "",
    });
  };

  return [formValues, handleSubmit, updateField, formError, loading, toDefault];
};
